import React from 'react';
import { Page, BlockCard, Button, TextField, showToast } from '@trellixio/roaster-coffee';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useAPI } from 'hooks';
import Locales from 'locales';

// Define the minimum valid campaign year as a constant
// This value is chosen based on the business requirement that the campaign year should not be earlier than 2023
// MIN_CAMPAIGN_YEAR is set to 2023 because it's the first year the platform was used for the Hajj
const CONST_MIN_CAMPAIGN_YEAR = 2023;
// Default pre-registration fee is 200000 (this can be adjusted based on the business requirement)
const CONST_DEFAULT_PRE_REGISTRATION_FEE = 200000;
// Default minimum number of pilgrims required for a certificate is 200 (this can be adjusted based on the business requirement)
const CONST_DEFAULT_MINIMUM_PILGRIMS = 200;
// Default minimum eligibility age for pilgrims is 2 (this can be adjusted based on the business requirement)
const CONST_ELIGIBILITY_AGE = 2;

const SuperAdminCampaign = ({ preferences }) => {
  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const {
    registration_deposit: registrationDeposit,
    certificate_min_active_pilgrim: certificateMinActivePilgrim,
    campaign_year: editableCampaignYear,
    pilgrim_eligibility_min_age: editableEligibilityAge,
  } = preferences || {};

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      registration_deposit: registrationDeposit || CONST_DEFAULT_PRE_REGISTRATION_FEE,
      certificate_min_active_pilgrim: certificateMinActivePilgrim || CONST_DEFAULT_MINIMUM_PILGRIMS,
      campaign_year: editableCampaignYear || new Date().getFullYear(),
      pilgrim_eligibility_min_age: editableEligibilityAge || CONST_ELIGIBILITY_AGE,
    },
  });

  const integerFieldValidator = {
    value: /^[0-9]+$/,
    message: Locales.campaign.error.invalid,
  };

  const requiredIntegerValidation = {
    required: Locales.campaign.error.invalid,
    pattern: integerFieldValidator,
  };

  const { mutate: editPreference } = useMutation({
    mutationFn: (data) => api.update('preference', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preference/current/'] });
      showToast({
        message: Locales.campaign.toast.success,
        level: 'success',
      });
    },
  });

  const onSubmit = (data) => editPreference(data);

  return (
    <Page loading={isMutating} title={`${Locales.campaign.title} ${editableCampaignYear}`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <BlockCard title={Locales.campaign.subtitle}>
          <Controller
            name="campaign_year"
            control={control}
            rules={{
              required: Locales.campaign.error.invalid,
              min: {
                value: CONST_MIN_CAMPAIGN_YEAR,
                message: Locales.campaign.error.out_of_range,
              },
              pattern: integerFieldValidator,
            }}
            render={({ field }) => (
              <TextField
                label={Locales.campaign.field.campaign_year}
                type="number"
                error={errors.campaign_year?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="pilgrim_eligibility_min_age"
            control={control}
            rules={requiredIntegerValidation}
            render={({ field }) => (
              <TextField
                label={Locales.campaign.field.min_eligibility_age}
                type="number"
                helpText={Locales.campaign.field.helptext.min_eligibility_age}
                error={errors.pilgrim_eligibility_min_age?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="registration_deposit"
            control={control}
            rules={requiredIntegerValidation}
            render={({ field }) => (
              <TextField
                label={Locales.campaign.field.pre_registration_fee}
                type="number"
                helpText={Locales.campaign.field.helptext.pre_registration_fee}
                prefix="CFA"
                className="prefixed-input"
                error={errors.registration_deposit?.message}
                {...field}
              />
            )}
          />

          <Controller
            name="certificate_min_active_pilgrim"
            control={control}
            rules={requiredIntegerValidation}
            render={({ field }) => (
              <TextField
                label={Locales.campaign.field.minimum_pilgrims}
                type="number"
                helpText={Locales.campaign.field.helptext.minimum_pilgrims}
                error={errors.certificate_min_active_pilgrim?.message}
                {...field}
              />
            )}
          />
        </BlockCard>

        <div className="form-actions">
          <span />
          <Button variant="primary" color="success" disabled={isMutating} submit>
            {Locales.base.button.save}
          </Button>
        </div>
      </form>
    </Page>
  );
};

SuperAdminCampaign.defaultProps = {
  preferences: null,
};

SuperAdminCampaign.propTypes = {
  preferences: PropTypes.shape({}),
};

export default SuperAdminCampaign;
